import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  CatalogProduct,
  formatMoney,
  getCatalogProduct,
  thumbLetter,
  useCatalogMap,
} from './catalog';
import { IconGift, IconInfo, IconVoucher } from './icons';
import { AppliedQuoteDiscount } from './pricing';
import {
  LiveProduct,
  LiveSessionDiscount,
  assignedProductIds,
  formatDropClock,
  isLiveVoucher,
  isStoreVoucher,
  sameProductId,
} from './utils';

interface Props {
  discounts: LiveSessionDiscount[];
  className?: string;
  /** Discount IDs currently applied for this product checkout */
  appliedDiscountIds?: string[];
  /** Fallback match when id missing — discount titles from calculator */
  appliedTitles?: string[];
  /** Full applied rows — ensures used vouchers appear even if missing from session list */
  appliedDiscounts?: AppliedQuoteDiscount[];
  /** PDP: + between a live voucher and a store voucher when both apply to this item */
  stackPlus?: boolean;
  /** Session products — used to name product-assigned vouchers */
  products?: LiveProduct[];
  /** Product currently open on PDP, if any */
  currentProductId?: string;
  /** Open an assigned product from the info tip */
  onOpenProduct?: (product: LiveProduct) => void;
}

function normId(id?: string | null): string {
  return String(id || '')
    .trim()
    .toLowerCase();
}

function normTitle(title?: string | null): string {
  return String(title || '')
    .trim()
    .toLowerCase();
}

function isApplied(
  d: LiveSessionDiscount,
  appliedIds: Set<string>,
  appliedTitles: Set<string>
): boolean {
  const id = normId(d.discount_id);
  if (id && appliedIds.has(id)) return true;
  const name = normTitle(d.discount_name);
  const display = normTitle(d.display_name);
  if (name && appliedTitles.has(name)) return true;
  if (display && appliedTitles.has(display)) return true;
  return false;
}

function isAvailable(d: LiveSessionDiscount): boolean {
  if (d.available === false) return false;
  if (d.discount_status && d.discount_status !== 'active') return false;
  return true;
}

function unavailableLabel(d: LiveSessionDiscount): string {
  switch (d.unavailable_reason) {
    case 'redeemed':
      return 'Fully redeemed';
    case 'expired':
      return 'Expired';
    case 'scheduled':
      return 'Coming soon';
    case 'inactive':
      return 'Inactive';
    default:
      return 'Unavailable';
  }
}

function ChipIcon({ type, size = 34 }: { type?: string | null; size?: number }) {
  if (type === 'free_gift' || type === 'bogo') {
    return <IconGift size={size} />;
  }
  return <IconVoucher size={size} />;
}

function toChipFromApplied(d: AppliedQuoteDiscount): LiveSessionDiscount {
  const isShip = d.discount_type === 'free_shipping';
  return {
    discount_id: d.discount_id,
    discount_name: d.discount_title,
    display_name: d.discount_title,
    discount_type: d.discount_type,
    discount_status: 'active',
    live_exclusive: Boolean(d.live_exclusive),
    display_off: isShip
      ? 'Free ship'
      : d.total_discount > 0
        ? `−RM ${formatMoney(d.total_discount)}`
        : 'Applied',
    display_min_spend: isShip ? 'No min. spend' : 'Applied on this item',
    available: true,
    unavailable_reason: null,
  };
}

function dropMeta(d: LiveSessionDiscount, nowMs: number): { time: string | null; qty: string | null } {
  const dropOn = Boolean(d.live_exclusive && d.drop_started_at);
  if (!dropOn) return { time: null, qty: null };
  let time: string | null = null;
  if (d.duration_minutes != null && d.drop_ends_at) {
    const end = new Date(d.drop_ends_at).getTime();
    if (!Number.isNaN(end)) {
      const remaining = end - nowMs;
      time = remaining <= 0 ? 'Ended' : formatDropClock(remaining);
    }
  }
  const qty =
    d.usage_limit != null && d.uses_remaining != null ? `${d.uses_remaining} left` : null;
  return { time, qty };
}

function assignedProduct(
  productId: string,
  products: LiveProduct[] | undefined,
  catalog: Map<string, CatalogProduct>
): { id: string; title: string; image: string | null } {
  const info = catalog.get(String(productId)) || getCatalogProduct(productId);
  const live = (products || []).find((p) => sameProductId(p.item_id, productId));
  return {
    id: String(productId),
    title: info?.title || live?.item_name || 'Assigned product',
    image: info?.image || null,
  };
}

function AssignedTip({
  productIds,
  products,
  catalog,
  currentProductId,
  onOpenProduct,
  open,
  onOpenChange,
  anchorRef,
}: {
  productIds: string[];
  products?: LiveProduct[];
  catalog: Map<string, CatalogProduct>;
  currentProductId?: string;
  onOpenProduct?: (product: LiveProduct) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef: { current: HTMLElement | null };
}) {
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const targets = productIds.map((id) => assignedProduct(id, products, catalog));

  const place = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const shell = el.closest('.il-sheet, .il-full') as HTMLElement | null;
    const bounds = (shell || document.documentElement).getBoundingClientRect();
    const width = 240;
    const tipH = Math.min(220, 36 + targets.length * 48);
    const left = Math.min(
      Math.max(bounds.left + 8, r.left),
      Math.max(bounds.left + 8, bounds.right - width - 8)
    );
    const below = r.bottom + 6;
    const top =
      below + tipH > bounds.bottom - 12
        ? Math.max(bounds.top + 8, r.top - tipH - 6)
        : below;
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    place();
    const onDoc = (e: MouseEvent) => {
      const node = e.target as Node | null;
      if (anchorRef.current?.contains(node) || tipRef.current?.contains(node)) return;
      onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    const onMove = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, targets.length]);

  const openProduct = (productId: string, title: string) => {
    const live =
      (products || []).find((p) => sameProductId(p.item_id, productId)) ||
      ({
        item_id: String(productId),
        item_name: title,
        item_type: 'product',
      } satisfies LiveProduct);
    onOpenProduct?.(live);
    onOpenChange(false);
  };

  return (
    <>
      <span className="il-voucher-info" aria-hidden>
        <IconInfo size={14} />
      </span>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tipRef}
            className={`il-voucher-star-tip${targets.length > 1 ? ' is-list' : ''}`}
            role="dialog"
            aria-label={
              targets.length === 1
                ? `Voucher applies to ${targets[0].title}`
                : `Voucher applies to ${targets.length} products`
            }
            style={{ top: pos.top, left: pos.left }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <span className="il-voucher-star-tip-label">
              {targets.length > 1 ? `Applies to ${targets.length} products` : 'Applies to'}
            </span>
            {targets.map((target) => {
              const isCurrent = Boolean(
                currentProductId && sameProductId(currentProductId, target.id)
              );
              const canOpen = Boolean(onOpenProduct && !isCurrent);
              return (
                <span key={target.id} className="il-voucher-star-tip-row">
                  <span className="il-voucher-star-tip-thumb" aria-hidden>
                    {target.image ? (
                      <img src={target.image} alt="" />
                    ) : (
                      <span className="il-voucher-star-tip-ph">{thumbLetter(target.title)}</span>
                    )}
                  </span>
                  {canOpen ? (
                    <button
                      type="button"
                      className="il-voucher-star-tip-name is-link"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openProduct(target.id, target.title);
                      }}
                    >
                      {target.title}
                    </button>
                  ) : (
                    <span className="il-voucher-star-tip-name">{target.title}</span>
                  )}
                </span>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

function VoucherChip({
  d,
  applied,
  nowMs,
  products,
  catalog,
  currentProductId,
  onOpenProduct,
}: {
  d: LiveSessionDiscount;
  applied: boolean;
  nowMs: number;
  products?: LiveProduct[];
  catalog: Map<string, CatalogProduct>;
  currentProductId?: string;
  onOpenProduct?: (product: LiveProduct) => void;
}) {
  const chipRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  const available = isAvailable(d);
  const name = d.display_name || d.discount_name || 'LIVE deal';
  const off = d.display_off || (d.discount_type === 'free_shipping' ? 'Free ship' : 'Deal');
  const minSpend =
    d.display_min_spend || (d.discount_type === 'free_shipping' ? 'No min. spend' : '\u00a0');
  const drop = dropMeta(d, nowMs);
  const assignedIds = assignedProductIds(d);
  const clickable = assignedIds.length > 0;
  const ChipTag = clickable ? 'button' : 'div';
  return (
    <ChipTag
      ref={chipRef as never}
      type={clickable ? 'button' : undefined}
      className={`il-voucher-chip${applied ? ' is-applied' : ''}${
        !available && !applied ? ' is-unavailable' : ''
      }${
        d.live_exclusive ? ' is-live-special' : d.discount_type === 'voucher' ? ' is-normal' : ''
      }${clickable ? ' is-clickable has-info' : ''}`}
      aria-disabled={!available && !applied}
      aria-expanded={clickable ? tipOpen : undefined}
      aria-label={
        clickable
          ? `${name}. Applies to ${assignedIds.length} product${assignedIds.length === 1 ? '' : 's'}`
          : undefined
      }
      title={
        applied
          ? 'Applied on this item'
          : !available
            ? unavailableLabel(d)
            : d.live_exclusive
              ? 'Live Special — only on LIVE checkout'
              : undefined
      }
      onClick={
        clickable
          ? () => setTipOpen((v) => !v)
          : undefined
      }
    >
      <div className="il-voucher-chip-text">
        <div className="il-voucher-chip-name">{name}</div>
        <div className="il-voucher-chip-off">{off}</div>
        {drop.time || drop.qty ? (
          <div className="il-voucher-chip-drop">
            {drop.time && <span className="il-voucher-chip-timer">{drop.time}</span>}
            {drop.qty && <span className="il-voucher-chip-qty">{drop.qty}</span>}
          </div>
        ) : (
          <div className="il-voucher-chip-min">{minSpend}</div>
        )}
      </div>
      <div className="il-voucher-chip-icon" aria-hidden>
        <ChipIcon type={d.discount_type} size={34} />
      </div>
      {d.live_exclusive && <span className="il-voucher-chip-live-special">Live Special</span>}
      {!d.live_exclusive && d.discount_type === 'voucher' && (
        <span className="il-voucher-chip-store">Store</span>
      )}
      {clickable && (
        <AssignedTip
          productIds={assignedIds}
          products={products}
          catalog={catalog}
          currentProductId={currentProductId}
          onOpenProduct={onOpenProduct}
          open={tipOpen}
          onOpenChange={setTipOpen}
          anchorRef={chipRef}
        />
      )}
      {applied && <span className="il-voucher-chip-applied">Applied</span>}
      {!available && !applied && (
        <span className="il-voucher-chip-unavailable">{unavailableLabel(d)}</span>
      )}
    </ChipTag>
  );
}

export function VoucherChips({
  discounts,
  className = '',
  appliedDiscountIds = [],
  appliedTitles = [],
  appliedDiscounts = [],
  stackPlus = false,
  products,
  currentProductId,
  onOpenProduct,
}: Props) {
  const catalog = useCatalogMap(products);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const idSet = new Set(
    [
      ...appliedDiscountIds,
      ...appliedDiscounts.map((d) => d.discount_id),
    ]
      .map(normId)
      .filter(Boolean)
  );
  const titleSet = new Set(
    [
      ...appliedTitles,
      ...appliedDiscounts.map((d) => d.discount_title),
    ]
      .map(normTitle)
      .filter(Boolean)
  );

  // Include applied vouchers that aren't already in the session discount list
  const existingIds = new Set(discounts.map((d) => normId(d.discount_id)).filter(Boolean));
  const merged: LiveSessionDiscount[] = [...discounts];
  for (const applied of appliedDiscounts) {
    const id = normId(applied.discount_id);
    if (!id || existingIds.has(id)) continue;
    merged.push(toChipFromApplied(applied));
    existingIds.add(id);
  }

  if (!merged.length) return null;

  // Applied → available → unavailable (greyed, behind)
  const sorted = [...merged].sort((a, b) => {
    const aAvail = isAvailable(a);
    const bAvail = isAvailable(b);
    const aOn = isApplied(a, idSet, titleSet) ? 0 : aAvail ? 1 : 2;
    const bOn = isApplied(b, idSet, titleSet) ? 0 : bAvail ? 1 : 2;
    return aOn - bOn;
  });

  const liveStack = stackPlus
    ? sorted.find((d) => isLiveVoucher(d) && isApplied(d, idSet, titleSet))
    : undefined;
  const storeStack = stackPlus
    ? sorted.find((d) => isStoreVoucher(d) && isApplied(d, idSet, titleSet))
    : undefined;
  const pairIds =
    liveStack && storeStack
      ? new Set([normId(liveStack.discount_id), normId(storeStack.discount_id)])
      : null;

  const chipProps = {
    nowMs,
    products,
    catalog,
    currentProductId,
    onOpenProduct,
  };

  const nodes: ReactNode[] = [];
  let pairEmitted = false;
  for (const d of sorted) {
    const id = normId(d.discount_id);
    if (pairIds && liveStack && storeStack && pairIds.has(id)) {
      if (!pairEmitted) {
        nodes.push(
          <div
            key={`stack-${liveStack.discount_id}-${storeStack.discount_id}`}
            className="il-voucher-stack is-leading"
          >
            <VoucherChip d={liveStack} applied {...chipProps} />
            <span className="il-voucher-plus" aria-hidden>
              +
            </span>
            <VoucherChip d={storeStack} applied {...chipProps} />
          </div>
        );
        pairEmitted = true;
      }
      continue;
    }
    nodes.push(
      <VoucherChip
        key={d.discount_id}
        d={d}
        applied={isApplied(d, idSet, titleSet)}
        {...chipProps}
      />
    );
  }

  return (
    <div className={`il-voucher-rail ${className}`.trim()} aria-label="LIVE vouchers">
      {nodes}
    </div>
  );
}
